import { useCallback, useRef, useState } from 'react'
import { DisabledReason } from '../../platform/access/DisabledReason'
import { Panel } from '../../platform/primitives/Panel'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { Select } from '../../platform/primitives/Select'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import '../../platform/primitives/field.css'
import { CategoryField } from '../categories/picker'
import { BrandSuggestion } from '../merchants/catalogue'
import { MerchantField, type PickerMerchant } from '../merchants/picker'
import { TagEditor } from '../../platform/primitives/TagEditor'
import type { Txn } from './transactionWire'
import { rebaseTags } from './rebaseTags'
import { useAutosaveField, type FieldKind, type SaveStatus } from './useAutosaveField'
import { useCategoryMemoryOffer } from './useCategoryMemoryOffer'
import { useFilterOptions } from './useFilterOptions'

/** The backend's whole-list ceiling for a transaction (`tags` is at most 50). */
const TAG_CEILING = { max: 50, note: 'A transaction can have up to 50 tags.' } as const

/** Q5. One sentence, shown once, and every disabled control points at it. */
export const PENDING_REASON = 'Pending — other edits are lost when the bank posts it'
const PENDING_ID = 'txn-edit-pending'
const NOTE_DEBOUNCE_MS = 800

/** The owner control's value for "Account default": `beneficial_owner_id: null`. An owner id is a UUID, so it cannot collide. */
const ACCOUNT_DEFAULT = 'account-default'

type Props = {
  txn: Txn
  /** After the server confirms a category change: what it was, and what it now is. */
  onCategoryChanged?: (previous: string | null, next: string | null) => void
}

/**
 * The always-editable "Details" panel (Q17): every field saves as it changes,
 * no Save button. A pending row (Q5) keeps Category editable and disables the
 * rest.
 *
 * A label/value list, not a form: each control keeps its own size (selects share
 * one 240px width, the owner segments hug their labels) and only the note uses
 * the column. On a phone the label sits above its control, and the owner
 * becomes the platform `Select` — five-word segments wrap into a two-row
 * capsule at 375px. The breakpoint is read once, here, from `useBreakpoint()`.
 */
export function EditPanel({ txn, onCategoryChanged }: Props) {
  const options = useFilterOptions()
  const [statuses, setStatuses] = useState<Partial<Record<FieldKind, SaveStatus>>>({})
  const onStatus = useCallback(
    (kind: FieldKind, status: SaveStatus) => setStatuses((s) => (s[kind] === status ? s : { ...s, [kind]: status })),
    [],
  )
  const pending = txn.isPending
  const phone = useBreakpoint() === 'phone'
  const inputHeight = useHitTarget('page')

  const categoryName = (id: string | null) =>
    id === null ? 'no category' : (options.categories.flatMap((g) => g.items).find((c) => c.id === id)?.label ?? 'its previous category')
  const memory = useCategoryMemoryOffer('detail')
  const category = useAutosaveField<string | null>(
    txn.id,
    'category',
    txn.categoryId,
    (v) => ({ category_id: v }),
    (t) => t.categoryId,
    {
      describe: categoryName,
      onStatus,
      onMemorySuggestion: memory.offer,
      onConfirmed: (confirmed, before) => {
        if (confirmed.categoryId !== before) onCategoryChanged?.(before, confirmed.categoryId)
      },
    },
  )
  const ownerName = (id: string | null) =>
    id === null ? 'the account default' : (options.owners.find((o) => o.id === id)?.label ?? 'its previous owner')
  const owner = useAutosaveField<string | null>(
    txn.id,
    'owner',
    txn.ownerId,
    (v) => ({ beneficial_owner_id: v }),
    (t) => t.ownerId,
    {
      describe: ownerName,
      onStatus,
    },
  )
  const tags = useAutosaveField<string[]>(
    txn.id,
    'tags',
    txn.tags,
    (v) => ({ tags: v }),
    (t) => t.tags,
    { onStatus, rebase: rebaseTags },
  )
  // Names for the merchants picked here, so a failed save can say what the field went back to; the row's
  // own merchant is named by the row. Written and read only in callbacks, never during render.
  const merchantNames = useRef(new Map<string, string>())
  // A brand added with "Also set category": the category waits for the merchant's save, and is set only
  // once the server confirms THAT merchant, so a failed merchant save never leaves the category alone.
  const categoryAfterMerchant = useRef<{ merchantId: string; categoryId: string } | null>(null)
  const merchantField = useRef<HTMLButtonElement>(null)
  const merchant = useAutosaveField<string | null>(
    txn.id,
    'merchant',
    txn.merchantId,
    (v) => ({ merchant_id: v }),
    (t) => t.merchantId,
    {
      describe: (id) =>
        id === null ? 'no merchant' : (merchantNames.current.get(id) ?? (id === txn.merchantId ? txn.merchant : 'its previous merchant')),
      onStatus,
      onConfirmed: (confirmed) => {
        const next = categoryAfterMerchant.current
        categoryAfterMerchant.current = null
        if (next !== null && confirmed.merchantId === next.merchantId) category.set(next.categoryId)
      },
    },
  )
  /** Sets the merchant, and the category a brand brought with it only after the merchant is saved. */
  const setMerchant = (id: string | null, extras?: { categoryId: string }) => {
    categoryAfterMerchant.current = id !== null && extras !== undefined ? { merchantId: id, categoryId: extras.categoryId } : null
    merchant.set(id)
  }
  const note = useAutosaveField<string>(
    txn.id,
    'note',
    txn.note ?? '',
    (v) => ({ user_notes: v.trim() === '' ? null : v }),
    (t) => t.note ?? '',
    { debounceMs: NOTE_DEBOUNCE_MS, onStatus },
  )

  // The row's own merchant names the field when the stored id is the row's — the unknown-current fallback
  // the `<select>` had: a merchant no list page holds (a global one, say) still reads by name.
  const currentMerchant: PickerMerchant | null =
    txn.merchantId === null
      ? null
      : { id: txn.merchantId, name: txn.merchant, identity: txn.merchantBrief ?? { id: txn.merchantId, name: txn.merchant, type: 'business', is_global: false } }

  const all = Object.values(statuses)
  const headline = all.includes('saving') ? 'Saving…' : all.includes('saved') ? 'Saved' : 'Changes save automatically'
  const reason = pending ? PENDING_ID : undefined
  const ownerOptions = [
    ...options.owners.map((o) => ({ value: o.id, label: o.label })),
    { value: ACCOUNT_DEFAULT, label: 'Account default' },
  ]

  const rows = (
    <>
      <div className="txn-edit__row">
        <span className="txn-edit__label">Merchant</span>
        <div className="txn-edit__value">
          <div className="txn-merchant-field">
            <MerchantField
              className="txn-edit__select"
              label="Merchant"
              value={merchant.value}
              current={currentMerchant}
              onChange={(id, picked, extras) => {
                if (picked !== undefined) merchantNames.current.set(picked.id, picked.name)
                setMerchant(id, extras)
              }}
              openerRef={merchantField}
              allowNone
              offerCategory={category.value === null}
              disabled={pending}
              describedBy={reason}
              context={{ merchant: txn.merchant, date: txn.date, amountCents: txn.amountCents, currency: txn.currency, raw: txn.raw }}
            />
            {/* The bank's own descriptor, beneath the field. */}
            <div className="txn-merchant-field__raw">{txn.raw}</div>
            {/* "Looks like <brand>": only while the row has no merchant, and never on a pending row, whose merchant
                cannot be set. Add assigns through this panel's own fields, as a pick from the field does. Not now
                and Add take it off the screen, so focus goes to the merchant field beside it. */}
            {merchant.value === null && !pending && (
              <BrandSuggestion
                name={txn.merchant}
                txnIds={[txn.id]}
                offerCategory={category.value === null}
                onAdded={(m, extras) => {
                  merchantNames.current.set(m.id, m.name)
                  setMerchant(m.id, extras)
                }}
                returnFocus={() => merchantField.current?.focus()}
              />
            )}
          </div>
        </div>
      </div>
      <div className="txn-edit__row">
        <span className="txn-edit__label">Owner</span>
        <div className="txn-edit__value">
          {phone ? (
            <Select
              className="txn-edit__select"
              label="Owner"
              value={owner.value ?? ACCOUNT_DEFAULT}
              disabled={pending}
              aria-describedby={reason}
              onChange={(e) => owner.set(e.target.value === ACCOUNT_DEFAULT ? null : e.target.value)}
            >
              {ownerOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          ) : (
            <SegmentedControl
              label="Owner"
              name="txn-owner"
              size="panel"
              options={ownerOptions}
              value={owner.value ?? ACCOUNT_DEFAULT}
              disabled={pending}
              describedBy={reason}
              onChange={(v) => owner.set(v === ACCOUNT_DEFAULT ? null : v)}
            />
          )}
        </div>
      </div>
      <div className="txn-edit__row">
        <span className="txn-edit__label">Tags</span>
        <div className="txn-edit__value">
          <TagEditor
            tags={tags.value}
            suggestions={options.tags}
            disabled={pending}
            reasonId={reason}
            onChange={tags.set}
            ceiling={TAG_CEILING}
          />
        </div>
      </div>
      <div className="txn-edit__row">
        <label className="txn-edit__label" htmlFor="txn-edit-note">
          Note
        </label>
        <div className="txn-edit__value">
          <textarea
            id="txn-edit-note"
            className="field__input txn-edit__note"
            style={{ minHeight: inputHeight }}
            rows={2}
            value={note.value}
            disabled={pending}
            aria-describedby={reason}
            autoComplete="off"
            onChange={(e) => note.set(e.target.value)}
            onBlur={note.flush}
          />
        </div>
      </div>
    </>
  )

  return (
    <Panel className={phone ? 'txn-edit txn-edit--stacked' : 'txn-edit'}>
      <div className="txn-edit__head">
        <h3>Details</h3>
        <span aria-live="polite" className="txn-edit__status">
          {headline}
        </span>
      </div>
      <div className="txn-edit__row">
        <span className="txn-edit__label">Category</span>
        <div className="txn-edit__value">
          <CategoryField
            className="txn-edit__select"
            label="Category"
            value={category.value}
            onChange={category.set}
            allowNone
            context={{ merchant: txn.merchant, date: txn.date, amountCents: txn.amountCents, currency: txn.currency }}
          />
        </div>
      </div>
      {pending ? (
        <DisabledReason reason={PENDING_REASON} reasonId={PENDING_ID}>
          {rows}
        </DisabledReason>
      ) : (
        rows
      )}
    </Panel>
  )
}
